import { createFileRoute, Link } from "@tanstack/react-router";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { AionCta } from "@/components/site/AionCta";
import { RelatedPages } from "@/components/site/RelatedPages";

const URL = "https://rajshamani.life/guides/how-to-become-an-angel-investor-india";
const TITLE = "How to Become an Angel Investor in India (2026 Guide)";
const DESC =
  "What it actually takes to angel invest in India: minimum cheque sizes, syndicates vs direct deals, deal flow, due diligence, taxes and the mistakes beginners make.";

const STEPS: { title: string; body: string }[] = [
  {
    title: "Be honest about the money you can lose",
    body: "Angel investing is the most illiquid asset most people will ever touch. A cheque is locked for five to ten years and the most likely outcome for any single startup is zero. The working rule used by experienced Indian angels is to commit only capital that would not change your life if it disappeared entirely — typically a small share of a portfolio that is already built on safer assets.",
  },
  {
    title: "Understand the minimums",
    body: "Direct angel cheques in India usually start around ₹5–25 lakh per company. Syndicates and angel platforms lower that meaningfully, often to ₹1–5 lakh per deal, by pooling many investors behind one lead. Starting through a syndicate is the sane path: you see real deal flow and real diligence before you risk writing a solo cheque.",
  },
  {
    title: "Get legitimate deal flow",
    body: "Good deals are rarely public. Deal flow comes from angel networks, accelerator demo days, operator communities, and — most reliably — being useful to founders before you have any money in the deal. If every startup you see is cold-emailing you, you are seeing the deals that better-connected investors already passed on.",
  },
  {
    title: "Diligence the founder before the deck",
    body: "At pre-seed and seed there is almost no data to analyse, so you are underwriting people. Check what they have shipped, how they talk about customers, how quickly they answer hard questions, and what past colleagues say unprompted. Then check the boring things: cap table, prior rounds, pending liabilities and whether the company is properly incorporated.",
  },
  {
    title: "Decide your cheque size and portfolio count first",
    body: "Angel returns follow a power law: a small number of companies return the whole portfolio. A concentrated portfolio of two or three names is closer to gambling than investing. Most angels plan for 15–25 cheques over several years, and reserve part of their capital for follow-on rounds in the companies that work.",
  },
  {
    title: "Know the paperwork and the tax treatment",
    body: "Indian angel deals typically use a SAFE-style instrument, a CCPS round or a straightforward equity round with a shareholders' agreement. Registering as an accredited investor or investing via a SEBI-registered AIF changes both the process and the tax position. Take professional advice on capital gains and reporting before your first cheque, not after.",
  },
  {
    title: "Add value or stay out of the way",
    body: "The angels founders come back to are the ones who make introductions, help with hiring and answer messages within a day. The rest are dead capital. Decide honestly which one you are — a passive angel investing through a syndicate is far more useful than an active angel who slows a company down.",
  },
];

const FAQS: { q: string; a: string }[] = [
  {
    q: "How much money do you need to start angel investing in India?",
    a: "Through a syndicate or angel platform you can start with roughly ₹1–5 lakh per deal. Direct cheques into a startup's round usually start around ₹5–25 lakh. Beyond the per-deal minimum, you need enough capital to make 15 or more investments over time, because returns depend on a small number of outliers.",
  },
  {
    q: "Do you need to be an accredited investor in India?",
    a: "Not for ordinary private-company investments, but accreditation and SEBI-registered AIF routes exist and affect eligibility for certain funds and the tax treatment of your returns. Confirm your position with a chartered accountant before investing.",
  },
  {
    q: "Is angel investing better than mutual funds?",
    a: "It is not a substitute. Mutual funds are liquid, diversified and regulated; angel investing is illiquid, concentrated and mostly loss-making at the individual-company level. Angels invest for the small chance of an outsized outcome, after their core portfolio is already in place.",
  },
  {
    q: "How do angel investors in India make money?",
    a: "Only through an exit — an acquisition, a secondary sale of shares to a later investor, or, rarely, an IPO. There are no dividends and no monthly returns. Timelines of five to ten years are normal.",
  },
  {
    q: "How many startups should an angel investor back?",
    a: "Most experienced angels target 15–25 companies to give the portfolio a realistic chance of catching an outlier, and keep reserves for follow-on rounds in the ones that show traction.",
  },
  {
    q: "What are the most common beginner mistakes?",
    a: "Investing too much in the first deal, backing friends without diligence, ignoring the cap table, chasing hyped sectors at inflated valuations, and assuming a warm intro is the same thing as validation.",
  },
];

export const Route = createFileRoute("/guides/how-to-become-an-angel-investor-india")({
  head: () => ({
    meta: [
      { key: "title", title: TITLE },
      { key: "description", name: "description", content: DESC },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: TITLE,
          description: DESC,
          author: { "@type": "Person", name: "Raj Shamani", url: "https://rajshamani.life" },
          step: STEPS.map((s) => ({
            "@type": "HowToStep",
            name: s.title,
            text: s.body,
          })),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: FAQS.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: AngelGuide,
});

function AngelGuide() {
  return (
    <div>
      <PageBreadcrumb current="How to Become an Angel Investor in India" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4">Guide</div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            How to become an <span className="text-gradient-gold">angel investor</span> in India
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Angel investing in India is more accessible than it was five years ago — syndicates have
            cut minimum cheques to a few lakh — and just as brutal as it always was. This guide
            covers what you need before your first cheque, how deal flow really works, and the
            mistakes that wipe out beginners.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-10">The seven steps</h2>
        <ol className="space-y-8">
          {STEPS.map((s, i) => (
            <li key={s.title} className="rounded-xl border border-border bg-card p-6">
              <div className="text-xs uppercase tracking-widest text-gold mb-2">
                Step {i + 1}
              </div>
              <h3 className="text-xl font-bold mb-3">{s.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{s.body}</p>
            </li>
          ))}
        </ol>
        <p className="mt-8 text-sm text-muted-foreground max-w-3xl">
          This guide is general information, not financial advice. Private-company investments can
          lose their entire value; consult a qualified adviser before investing.
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">Learn from people who do it</h2>
        <p className="text-muted-foreground max-w-3xl leading-relaxed">
          Raj Shamani has backed 20+ early-stage Indian startups and co-founded{" "}
          <Link to="/house-of-x" className="text-gold underline underline-offset-4">
            House of X
          </Link>
          , a D2C venture studio. Many{" "}
          <Link to="/raj-shamani-podcast" className="text-gold underline underline-offset-4">
            Figuring Out episodes
          </Link>{" "}
          are with founders and investors talking openly about valuations, failures and exits — the
          cheapest diligence education available. See also the{" "}
          <Link to="/guides/d2c-brands-india" className="text-gold underline underline-offset-4">
            D2C brands in India guide
          </Link>
          .
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {FAQS.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedPages pages={["ventures", "podcast", "book", "about", "aion", "contact"]} />
    </div>
  );
}
